<template>
  <section>
    <div class="ac-order-card container just-center ac-card-content">
      <div class="padding-default mt-5">
        <button
          type="button"
          class="sac__font-14 med underline f-primary"
          @click="$router.go(-1)"
        >
          <span class="icon-Arrow-Left sac__font-16" /> Back to Step 1: Branch Billing Setup
        </button>
        <div class="sac__font-22 med mt-2">
          Step 2 of 2: Payment Setup
        </div>
      </div>
      <div class="ac-body-container flex-column">
        <div class="grid-row gap-16">
          <div
            v-for="branch in branches"
            :key="branch.id"
            class="bordered-card"
          >
            <div class="flex-row align-center just-between">
              <div class="sac__font-22 semi-bold mb-2">
                {{ getBranch(branch.id)?.label }}
              </div>
              <div class="sac__font-22 semi-bold">
                Total Due Now: ${{ getNumberLocale(totalDueToday(branch)) }}
              </div>
            </div>
            <PaymentInfoSetting
              :branch="branch"
            />
            <InvoiceInput
              :branch="branch"
            />
            <div 
              v-if="showInitialPayment(branch) && (branch.orderPayee || branch.msaPayee)" 
              class="margin-top-24"
            >
              <div>
                Initial Payment
              </div>
              <div class="mt-2">
                <div class="grid-column first-large-3 med sac__font-14 med">
                  <div class="grid-item js-s">
                    Item
                  </div>
                  <div class="grid-item js-c">
                    Total
                  </div>
                  <div class="grid-item js-e">
                    Billed on
                  </div>
                </div>
              </div>
              <div v-if="branch.msaPayee">
                <div
                  v-for="(order, i) in agreement?.agreementOrders"
                  :key="i"
                  class="ao-table-order-body sac__font-14"
                  v-if="subscriptionTotal(order) > 0"
                >
                  <div class="grid-item js-s">
                    Order {{ i + 1 }} Initial Payment
                  </div>
                  <div class="grid-item js-c">
                    ${{ getNumberLocale(subscriptionTotal(order)) }}
                  </div>
                  <div class="grid-item js-e">
                    Today
                  </div>
                </div>
                <div
                  v-for="(order, i) in agreement?.agreementOrders"
                  :key="`one-time-${i}`"
                  class="ao-table-order-body sac__font-14"
                  v-if="oneTimeInitialTotal(order) > 0"
                >
                  <div class="grid-item js-s">
                    Order {{ i + 1 }} Initial Payment
                  </div>
                  <div class="grid-item js-c">
                    ${{ getNumberLocale(oneTimeInitialTotal(order)) }}
                  </div>
                  <div class="grid-item js-e">
                    Today
                  </div>
                </div>
              </div>
              <OrderLineItem
                v-if="agreementTotal(order) > 0"
                v-for="(order, i) in hardwareOrders(branch)"
                :key="i"
                name="Additional Accessories/Shipping & Taxes"
                :quantity="`$${getNumberLocale(agreementTotal(order))}`"
                :total="checkStartDate(order.plannedStartDate)
                  ? 'Today'
                  : `${ fullDateFormat(order.plannedStartDate) } (estimate)`"
              />
            </div>
            <div
              v-if="branch.subscriptionPayee && hasSubscriptionLines(branch)"
              class="margin-top-24"
            >
              <div class="sac__font-16">
                Subscriptions
              </div>
              <div class="mt-2">
                <div class="grid-column even-4 med sac__font-14 med">
                  <div class="grid-item js-s">
                    Item
                  </div>
                  <div class="grid-item js-c">
                    Quantity
                  </div>
                  <div class="grid-item js-c">
                    Monthly Payment
                  </div>
                  <div class="grid-item js-e">
                    Billing starts on
                  </div>
                </div>
              </div>
              <div
                v-for="(order, i) in subscriptionOrders(branch)"
                :key="i"
              >
                <div
                  v-for="ol in subscriptionOrderlines(order)"
                  :key="ol.agreementLineId"
                  class="grid-column even-4 sac__font-14 mt-2"
                >
                  <div 
                    v-if="ol.sku.includes('PM')"
                    class="grid-item js-s"
                  >
                    Order {{ agreement?.agreementOrders.indexOf(order) + 1 }}:
                    SmartAC.com Detect Subscription Monitoring
                  </div>
                  <div 
                    v-else-if="ol.sku.includes('PLAT')"
                    class="grid-item js-s"
                  >
                    Order {{ agreement?.agreementOrders.indexOf(order) + 1 }}:
                    SmartAC.com Platform Fee
                  </div>
                  <div 
                    v-else-if="ol.sku.includes('CNX')"
                    class="grid-item js-s"
                  >
                    Order {{ agreement?.agreementOrders.indexOf(order) + 1 }}:
                    SmartAC.com Connect Subscription
                  </div>
                  <div 
                    v-if="ol.sku.includes('CNX')"
                    class="grid-item js-c"
                  >
                    {{ ol.freeQuantity > 0 ? `${ol.freeQuantity} free user(s)` : '--' }}
                  </div>
                  <div 
                    v-else
                    class="grid-item js-c"
                  >
                    {{ ol.quantity }}
                  </div>
                  <div 
                    v-if="ol.sku.includes('PM')"
                    class="grid-item js-c"
                  >
                    ${{ getNumberLocale(detectTotal(order)) }}
                  </div>
                  <div 
                    v-else-if="ol.sku.includes('PLAT')"
                    class="grid-item js-c"
                  >
                    ${{ ol.total }}
                  </div>
                  <div 
                    v-else-if="ol.sku.includes('CNX')"
                    class="grid-item js-c"
                  >
                    {{ ol.price > 0 ? `$${ol.price} per user` : 'Free' }}
                  </div>
                  <div 
                    v-if="ol.sku.includes('PM')"
                    class="grid-item js-e"
                  >
                    {{ subscriptionDate(order) }} (estimate)
                  </div>
                  <div 
                    v-else-if="ol.sku.includes('PLAT')"
                    class="grid-item js-e"
                  >
                    {{ fullDateFormat(order.plannedStartDate) }} (estimate)
                  </div>
                  <div 
                    v-else-if="ol.sku.includes('CNX')"
                    class="grid-item js-e"
                  >
                    -
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="footer-container jc-e flex-row just-end">
            <button
              type="button"
              :class="['sac__button bg-primary', true ? '' : 'disabled']"
              :disabled="disableButton"
              @click="completeAgreement"
            >
              Confirm and Pay
            </button>
          </div>
        </div>
      </div>
    </div>
    <ActionModal />
    <OrderProcessingOverlay
      :show="processing"
    />
  </section>
</template>

<script>
import moment from 'moment';
import { mapState, mapGetters } from 'vuex';

import partnerAgreementMixin from '@shared/mixins/partnerAgreementMixin';
import unitPreferencesMixin from '@shared/mixins/unitPreferencesMixin';
import InvoiceInput from '@shared/components/partnerAgreement/InvoiceInput.vue';
import OrderProcessingOverlay from '@shared/components/shared/billingModal/OrderProcessingOverlay.vue';
import PaymentInfoSetting from '@shared/components/partnerAgreement/PaymentInfoSetting.vue';
import ActionModal from '@shared/components/partnerAgreement/ActionModal.vue';
import OrderLineItem from '@shared/components/partnerAgreement/OrderLineItem.vue';

export default {
  name: 'PaymentSetupView',
  components: {
    InvoiceInput,
    PaymentInfoSetting,
    ActionModal,
    OrderProcessingOverlay,
    OrderLineItem,
  },
  mixins: [partnerAgreementMixin, unitPreferencesMixin],
  data() {
    return {
      error: '',
      processing: false,
    };
  },
  computed: {
    ...mapState({
      agreement: (state) => state.partnerAgreement.agreement,
      selectedPaymentMethods: (state) => state.partnerAgreement.selectedPaymentMethods,
      allTaxExemptions: (state) => state.partnerAgreement.allTaxExemptions,
      payee: (state) => state.partnerAgreement.payee,
    }),
    ...mapGetters([
      'allBranches',
      'totalDeposit',
    ]),
    branches() {
      const branches = [];
      if (this.payee) {
        Object.keys(this.payee).forEach((x) => {
          const val = this.payee[x];
          let branch = branches.find((b) => b.id === val);
          if (branch) {
            const splitter = x.split('-');
            if (branch[splitter[0]]) {
              branch[splitter[0]].branchId = this.payee[x];
            } else {
              branch[splitter[0]] = { branchId: this.payee[x], agreementOrderIds: [] };
            }
            if (splitter[1]) {
              branch[splitter[0]].agreementOrderIds.push(splitter[1]);
            }
          } else {
            branch = { id: this.payee[x] };
            const splitter = x.split('-');
            branch[splitter[0]] = { branchId: this.payee[x], agreementOrderIds: [] };
            if (splitter[1]) {
              branch[splitter[0]].agreementOrderIds.push(splitter[1]);
            }
            branches.push(branch);
          }
        });
      }
      return branches;
    },
    agreementOrders() {
      return this.agreement?.agreementOrders || [];
    },
    disableButton() {
      let disable = false;
      Object.values(this.selectedPaymentMethods).forEach((x) => {
        if (x === null) {
          disable = true;
        }
      });
      Object.values(this.allTaxExemptions).forEach((x) => {
        const {
          exempt,
          number,
          reason,
        } = x;
        if (exempt && (!number || !reason)) {
          disable = true;
        }
      });
      return disable;
    },
  },
  async mounted() {
    if (!this.payee?.msaPayee) {
      this.$router.replace(`/agreement/${this.$route.params.agreementId}/setup`);
    } else {
      this.$store.dispatch('getTaxExemptCategory');
      await this.$store.dispatch('getAgreementPaymentMethods');
      this.$store.dispatch('getAgreementOrderTax');
    }
  },
  methods: {
    showInitialPayment(branch) {
      const branchOrders = this.agreement?.agreementOrders?.filter((x) => (
        branch?.orderPayee?.agreementOrderIds?.includes(`${x.agreementOrderId}`) || branch?.msaPayee?.agreementOrderIds?.includes(`${x.agreementOrderId}`)
      ));
      return branchOrders?.some((order) => (this.detectTotal(order) > 0 || this.hasHardwareLine(order)));
    },
    subscriptionTotal(order) {
      const {
        subscriptionTax,
      } = order;
      let total = this.detectTotal(order);
      if (subscriptionTax) {
        total += subscriptionTax;
      }
      return total;
    },
    detectTotal(order) {
      const {
        agreementOrderLines,
        subscriptionTax,
      } = order;
      const detect = agreementOrderLines.find((ol) => ol.sku.includes('PM'));
      if (!detect) {
        return 0;
      }
      let total = detect.total;
      if (subscriptionTax) {
        total += subscriptionTax;
      }
      return total;
    },
    oneTimeInitialTotal(order) {
      const {
        agreementOrderLines,
        subscriptionTax,
      } = order;
      const [firstLine] = agreementOrderLines;
      const hasDetect = agreementOrderLines.some((ol) => ol.sku.includes('PM'));
      // The first line is treated as the deposit and counted in totalDeposit /
      // Total Due Today. When there is no Detect membership it is a one-time
      // charge (e.g. Gen2 preorder) that no subscription row renders, so show it
      // here to match the total.
      if (hasDetect || !firstLine || firstLine.subscriptionPlanId) {
        return 0;
      }
      let total = firstLine.total;
      if (subscriptionTax) {
        total += subscriptionTax;
      }
      return total;
    },
    subscriptionDate(order) {
      return this.fullDateFormat(moment(order.plannedStartDate).add('days', order.deploymentPeriod));
    },
    hasHardwareLine(order) {
      return order.shippingCost || order.agreementOrderLines.some((ol) => !ol.subscriptionPlanId);
    },
    agreementTotal(order) {
      const {
        agreementOrderLines,
        oneTimeTax,
        shippingCost,
      } = order;
      let oTotal = agreementOrderLines.slice(1).reduce((acc, ol) => {
        let total = acc;
        if (!ol.subscriptionPlanId) {
          total += ol.total;
        }
        return total;
      }, 0);
      if (shippingCost) {
        oTotal += shippingCost;
      }
      if (oneTimeTax) {
        oTotal += oneTimeTax;
      }
      return oTotal;
    },
    getBranch(id) {
      return this.allBranches()?.find((x) => x.value === id);
    },
    checkStartDate(date) {
      const diff = moment(date).diff(moment(), 'days');
      return diff <= 0;
    },
    hardwareOrders(branch) {
      return this.agreement?.agreementOrders?.filter((x) => branch?.orderPayee?.agreementOrderIds?.includes(`${x.agreementOrderId}`));
    },
    hardwareOrderLines(branch) {
      const orders = this.hardwareOrders(branch);
      const orderLines = orders.reduce((acc, o) => {
        const orderL = o.agreementOrderLines.slice(1).map((ol) => ({
          ...ol,
          plannedStartDate: o.plannedStartDate,
        }));
        acc.push(...orderL);
        return acc;
      }, []);
      return orderLines;
    },
    subscriptionOrders(branch) {
      return this.agreement?.agreementOrders?.filter((x) => branch.subscriptionPayee.agreementOrderIds.includes(`${x.agreementOrderId}`));
    },
    subscriptionOrderlines(order) {
      return order.agreementOrderLines.filter((ol) => ol.subscriptionPlanId);
    },
    hasSubscriptionLines(branch) {
      return this.subscriptionOrders(branch)?.some(
        (order) => this.subscriptionOrderlines(order).length > 0,
      );
    },
    totalDueToday(branch) {
      let dueToday = this.hardwareOrders(branch).reduce((acc, o) => {
        const {
          agreementOrderLines,
          shippingCost,
          oneTimeTax,
        } = o;
        let due = acc;
        const startDateDue = this.checkStartDate(o.plannedStartDate);
        if (startDateDue) {
          due += agreementOrderLines.slice(1).reduce((ac, ol) => {
            let t = ac;
            if (!ol.subscriptionPlanId || ol.subscriptionPlanId?.includes('PLAT')) {
              t += ol.total;
            }
            return t;
          }, 0);
          if (shippingCost) {
            due += shippingCost;
          }
          if (oneTimeTax) {
            due += oneTimeTax;
          }
        }
        return due;
      }, 0);
      if (this.payee?.msaPayee === branch.id) {
        dueToday += this.totalDeposit;
      }
      return dueToday;
    },
    async completeAgreement() {
      this.processing = true;
      try {
        await this.$store.dispatch('completeAgreementSetup');
        this.$store.dispatch('showSuccessToast', {
          message: `Agreement ${this.agreement.agreementId} has been confirmed.`,
        });
        this.$router.replace('/settings?setting=agreements');
      } catch (err) {
        console.error(err);
      }
      this.processing = false;
    },
    shippingAddress(order) {
      const {
        shippingAddress: {
          address1,
          address2,
          city,
          state,
          zip,
        },
      } = order;
      return `${address1}${address2 ? ` ${address2}` : ''}, ${city}, ${state} ${zip}`;
    },
  },
};
</script>

<style scoped lang="scss">
section {
  @include section-container;
  background-color: $WHITE;
  .ao-table-order-body {
    margin-top: 8px;
    display: grid;
    padding: 8px;
    grid-template-columns: 1.2fr 1fr 1fr;
    background-color: $NBLUE15;

    &:nth-child(even) {
      background-color: $WHITE;
    }
  }

  .order-detail-container {
    margin-top: 5px;

    .ord-row:nth-child(odd) {
      padding: 8px;
      font-size: 14px;
      background-color: $NBLUE15;
    }

    .ord-row:nth-child(even) {
      padding: 8px;
      font-size: 14px;
      background-color: $WHITE;
    }

    .ord-row:first-child {
      border-top-left-radius: 8px;
      border-top-right-radius: 8px;
    }

    .ord-row:last-child {
      border-bottom-left-radius: 8px;
      border-bottom-right-radius: 8px;
    }

    .ao-table-body {
      display: grid;
      padding: 8px;
      grid-template-columns: 1fr 1fr 1fr;
    }
  }

  .order-detail-container .deposit-container {
    padding: 8px;
    display: grid;
    grid-auto-flow: column;
    justify-content: space-between;
    background-color: #002E63;
    color: $WHITE;
    font-size: 14px;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }

  .bordered-card {
    border-radius: 10px;
    padding: 15px;
    border: 1.5px solid $N30;
  }

  .subscriptions {
    background-color: $NBLUE15;
    border-radius: 4px;
    display: grid;
    padding: 8px;
    grid-template-columns: 1fr 1fr 1fr;
  }

  .dropdown {
    width: 300px;
  }

  .container {
    background-color: $WHITE;
    width: 100%;
  }
}
</style>
